import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import PrivacyModeDropdown from './PrivacyModeDropdown';
import FriendsList from './FriendsList';
import AdminDebugPanel from './AdminDebugPanel';
import FeedbackPanel from './FeedbackPanel';
import DatabaseTest from './DatabaseTest';
import OnboardingManager from './OnboardingManager';
import { useRealTimeInterests } from '../hooks/useRealTimeInterests';
import { canAccessDebugPanel, canAccessFeedbackPanel, isTester } from '../utils/userRoles';
import './MainAppV2.css';

// Optimized countdown component that manages its own timer
const CountdownTimer = ({ expirationTime }) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  
  useEffect(() => {
    if (!expirationTime) {
      setTimeRemaining('');
      return;
    }
    
    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expirationTime);
      const diff = expiry - now;
      
      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }
      
      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (minutes > 0) {
        setTimeRemaining(`${minutes} min${minutes !== 1 ? 's' : ''} left`);
      } else {
        setTimeRemaining(`${seconds} sec${seconds !== 1 ? 's' : ''} left`);
      }
    };
    
    // Update immediately
    updateTimer();
    
    // REAL-TIME OPTIMIZED: Reduced frequency, no mobile-specific logic needed
    const updateInterval = 10000; // 10 seconds - much less frequent since we have real-time updates
    
    const interval = setInterval(updateTimer, updateInterval);
    
    return () => clearInterval(interval);
  }, [expirationTime]);
  
  if (!timeRemaining) return null;
  
  return (
    <div className="interest-count-badge">
      <span className="time-remaining">{timeRemaining}</span>
    </div>
  );
};

const MainAppV2 = ({ user, onLogout, supabaseIntegration }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [searchRadius, setSearchRadius] = useState(8047); // 5 miles in meters
  const [placeType, setPlaceType] = useState('restaurant');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showDistanceMenu, setShowDistanceMenu] = useState(false);
  const [showPlaceTypeMenu, setShowPlaceTypeMenu] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [currentPrivacyMode, setCurrentPrivacyMode] = useState('public');
  const [expandedPlace, setExpandedPlace] = useState(null);
  const [lastPanTime, setLastPanTime] = useState(0);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);

  const { profile } = supabaseIntegration;

  // REAL-TIME: Replace polling with WebSocket subscriptions
  const {
    userInterests: interestedPlaces,
    userInterestExpirations,
    placeInterestCounts,
    setPlaceInterestCounts,
    loadPlaceInterestCounts,
    isConnected: interestsConnected,
    refresh: refreshInterests
  } = useRealTimeInterests(supabaseIntegration, user?.sub);

  // Expose real-time data for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.popmates_debug_realtime_interests = {
        userInterests: interestedPlaces,
        userInterestExpirations,
        placeInterestCounts,
        isConnected: interestsConnected,
        lastUpdate: new Date().toISOString(),
        refresh: refreshInterests // Expose the refresh function for debugging
      };
    }
  }, [interestedPlaces, userInterestExpirations, placeInterestCounts, interestsConnected, refreshInterests]);

  // Helper function to calculate distance between two points in miles
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Helper function to format distance in miles
  const formatDistance = (distanceInMiles) => {
    if (distanceInMiles < 0.1) {
      return `${Math.round(distanceInMiles * 5280)} ft`;
    } else if (distanceInMiles < 1) {
      return `${(distanceInMiles).toFixed(1)} mi`;
    } else {
      return `${Math.round(distanceInMiles * 10) / 10} mi`;
    }
  };

  // Helper function to get distance text for place cards
  const getPlaceDistance = (place) => {
    if (!userLocation || !place.location) return null;
    
    const distance = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      place.location.latitude,
      place.location.longitude
    );
    
    return formatDistance(distance);
  };

  // Initialize privacy mode from profile
  useEffect(() => {
    if (profile?.default_privacy_mode) {
      setCurrentPrivacyMode(profile.default_privacy_mode);
    }
  }, [profile]);

  // When nearby places change, load their interest counts
  useEffect(() => {
    if (nearbyPlaces.length > 0 && loadPlaceInterestCounts) {
      const googlePlaceIds = nearbyPlaces.map(place => place.id);
      console.log('🔍 Loading place interest counts for', googlePlaceIds.length, 'new places');
      loadPlaceInterestCounts(googlePlaceIds);
    }
  }, [nearbyPlaces, loadPlaceInterestCounts]);

  // Initialize Google Maps
  useEffect(() => {
    const initMap = () => {
      if (!window.google || !window.google.maps || !window.google.maps.Map) {
        setError('Google Maps API not fully loaded');
        return;
      }

      if (!mapRef.current) {
        setError('Map container not found');
        return;
      }

      try {
        const defaultLocation = { lat: 37.7749, lng: -122.4194 };
        
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: defaultLocation,
          zoom: 15,
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          zoomControl: false,
          gestureHandling: 'greedy',
          styles: [
            {
              featureType: "poi",
              elementType: "all",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        setMap(mapInstance);
        setError(null);
        setIsInitializing(false);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userPos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              console.log('🌍 Geolocation detected:', userPos, 'Accuracy:', position.coords.accuracy + 'm');
              setUserLocation(userPos);
              mapInstance.setCenter(userPos);
              
              new window.google.maps.Marker({
                position: userPos,
                map: mapInstance,
                title: 'Your Location',
                icon: {
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <!-- Outer pulsing ring -->
                      <circle cx="20" cy="20" r="18" fill="#3B82F6" fill-opacity="0.2" stroke="#3B82F6" stroke-width="2" stroke-opacity="0.4">
                        <animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite"/>
                      </circle>
                      <!-- Middle ring -->
                      <circle cx="20" cy="20" r="12" fill="#3B82F6" fill-opacity="0.3" stroke="#FFFFFF" stroke-width="2"/>
                      <!-- Inner dot -->
                      <circle cx="20" cy="20" r="6" fill="#3B82F6" stroke="#FFFFFF" stroke-width="2"/>
                      <!-- Center dot -->
                      <circle cx="20" cy="20" r="3" fill="#FFFFFF"/>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20),
                },
                zIndex: 1000,
              });

              console.log('📍 User location obtained, auto-search will be triggered by useEffect');
            },
            (error) => {
              console.warn('🚫 Geolocation error:', error.message, 'Code:', error.code);
              console.log('🌍 Using default location (San Francisco) instead');
            }
          );
        }
      } catch (error) {
        setError(`Failed to initialize map: ${error.message}`);
        setIsInitializing(false);
      }
    };

    if (!process.env.REACT_APP_GOOGLE_API_KEY) {
      setError('Google API key not configured');
      setIsInitializing(false);
      return;
    }

    if (!window.google || !window.google.maps) {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', initMap);
        existingScript.addEventListener('error', () => {
          setError('Failed to load Google Maps API');
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      window.initGoogleMaps = () => {
        initMap();
        delete window.initGoogleMaps;
      };
      
      script.onerror = () => {
        setError('Failed to load Google Maps API');
        delete window.initGoogleMaps;
      };
      
      document.head.appendChild(script);
    } else {
      initMap();
    }

    return () => {
      if (window.initGoogleMaps) {
        delete window.initGoogleMaps;
      }
    };
  }, []);

  // Function to fit map bounds to show all markers
  const fitMapToMarkers = (markers, userLocation) => {
    if (!map || markers.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    
    if (userLocation) {
      bounds.extend(new window.google.maps.LatLng(userLocation.lat, userLocation.lng));
    }
    
    markers.forEach(marker => {
      bounds.extend(marker.getPosition());
    });
    
    map.fitBounds(bounds, {
      padding: { top: 50, right: 50, bottom: 50, left: 50 }
    });
    
    const listener = window.google.maps.event.addListener(map, 'bounds_changed', () => {
      if (map.getZoom() > 18) {
        map.setZoom(18);
      }
      window.google.maps.event.removeListener(listener);
    });
  };

  // Search for nearby places
  const searchNearbyPlaces = async () => {
    if (!map) {
      setError('Maps not initialized');
      return;
    }

    setLoading(true);
    setError(null);
    setNearbyPlaces([]);

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);

    let latitude, longitude;
    if (userLocation) {
      latitude = userLocation.lat;
      longitude = userLocation.lng;
    } else {
      const center = map.getCenter();
      latitude = center.lat();
      longitude = center.lng();
    }
    
    const getIncludedTypes = (type) => {
      const typeMapping = {
        'restaurant': {
          includedTypes: ['afghani_restaurant', 'african_restaurant', 'american_restaurant', 'asian_restaurant', 'barbecue_restaurant', 'brazilian_restaurant', 'breakfast_restaurant', 'brunch_restaurant', 'buffet_restaurant', 'chinese_restaurant', 'dessert_restaurant', 'fine_dining_restaurant', 'french_restaurant', 'greek_restaurant', 'hamburger_restaurant', 'indian_restaurant', 'indonesian_restaurant', 'italian_restaurant', 'japanese_restaurant', 'korean_restaurant', 'lebanese_restaurant', 'mediterranean_restaurant', 'mexican_restaurant', 'middle_eastern_restaurant', 'pizza_restaurant', 'ramen_restaurant', 'seafood_restaurant', 'spanish_restaurant', 'sushi_restaurant', 'thai_restaurant', 'turkish_restaurant', 'vegan_restaurant', 'vegetarian_restaurant', 'vietnamese_restaurant', 'restaurant'],
          excludedTypes: ['fast_food_restaurant', 'supermarket', 'food_store', 'store', 'amusement_park', 'amusement_center', 'video_arcade']
        },
        'cafe': { includedTypes: ['cafe', 'coffee_shop', 'tea_house'], excludedTypes: ['fast_food_restaurant'] },
        'bar': { includedTypes: ['bar', 'wine_bar', 'bar_and_grill', 'pub'] },
        'arts_culture': { includedTypes: ['art_gallery', 'art_studio', 'auditorium', 'cultural_landmark', 'historical_place', 'monument', 'museum', 'performing_arts_theater', 'sculpture'] },
        'dessert': { includedTypes: ['dessert_shop', 'dessert_restaurant', 'acai_shop', 'ice_cream_shop'] },
        'entertainment': { 
          includedTypes: ['amusement_center', 'amusement_park', 'bowling_alley', 'movie_theater', 'tourist_attraction', 'video_arcade', 'water_park', 'ferris_wheel', 'comedy_club', 'concert_hall', 'opera_house'],
          excludedTypes: ['aquarium', 'botanical_garden', 'dog_park', 'garden', 'hiking_area', 'national_park', 'park', 'state_park', 'wildlife_park', 'wildlife_refuge', 'zoo', 'beach']
        },
        'fast_food': { includedTypes: ['fast_food_restaurant'] },
        'nature': { 
          includedTypes: ['aquarium', 'botanical_garden', 'dog_park', 'garden', 'hiking_area', 'national_park', 'park', 'state_park', 'wildlife_park', 'wildlife_refuge', 'zoo', 'beach'],
          excludedTypes: ['amusement_center', 'amusement_park', 'bowling_alley', 'movie_theater', 'tourist_attraction', 'video_arcade', 'water_park', 'ferris_wheel', 'comedy_club', 'concert_hall', 'opera_house']
        },
        'fitness': { includedTypes: ['adventure_sports_center', 'cycling_park', 'skateboard_park', 'off_roading_area', 'arena', 'athletic_field', 'fishing_charter', 'fishing_pond', 'fitness_center', 'golf_course', 'gym', 'ice_skating_rink', 'playground', 'ski_resort', 'sports_activity_location', 'sports_club', 'sports_coaching', 'sports_complex', 'stadium', 'swimming_pool'] }
      };
      const mapping = typeMapping[type];
      if (mapping) {
        if (Array.isArray(mapping)) { // Should not happen with new structure, but safe
          return { includedTypes: mapping };
        }
        return mapping; // This is now an object like { includedTypes: [...], excludedTypes: [...] } or { includedTypes: [...] }
      }
      return { includedTypes: ['restaurant'] }; // Default fallback
    };

    const typeConfig = getIncludedTypes(placeType);

    const requestBody = {
      includedTypes: typeConfig.includedTypes,
      maxResultCount: 20,
      locationRestriction: {
        circle: {
          center: {
            latitude: latitude,
            longitude: longitude
          },
          radius: searchRadius
        }
      },
      languageCode: 'en'
    };

    if (typeConfig.excludedTypes && typeConfig.excludedTypes.length > 0) {
      requestBody.excludedTypes = typeConfig.excludedTypes;
    }

    try {
      const response = await fetch('https://places.googleapis.com/v1/places:searchNearby', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
          'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.rating,places.priceLevel,places.types,places.id'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Places API error: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      
      if (data.places && data.places.length > 0) {
        setNearbyPlaces(data.places);
        
        // Create new markers and store them for cleanup
        const newMarkers = [];
        data.places.forEach((place, index) => {
          const markerColor = getMarkerColor(place);
          
          const marker = new window.google.maps.Marker({
            position: {
              lat: place.location.latitude,
              lng: place.location.longitude
            },
            map: map,
            title: place.displayName?.text || 'Unknown Place',
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2C9.58 2 6 5.58 6 10C6 16 14 26 14 26S22 16 22 10C22 5.58 18.42 2 14 2Z" fill="${markerColor}"/>
                  <circle cx="14" cy="10" r="3" fill="white"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(28, 28),
            },
          });
          
          marker.customCurrentColor = markerColor;

          marker.addListener('click', () => {
            setSelectedPlace(place);
            setExpandedPlace(place);
            
            smartPanToPlace(place);
            
            setTimeout(() => {
              const placeElement = document.getElementById(`place-${place.id}`);
              if (placeElement) {
                placeElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
            }, 100);
          });

          newMarkers.push(marker);
        });
        
        setMarkers(newMarkers);
        
        if (markers.length === 0 && newMarkers.length > 0) {
          console.log('🗺️ Initial map fitting for', newMarkers.length, 'markers');
          fitMapToMarkers(newMarkers, userLocation);
        }
      } else {
        setNearbyPlaces([]);
        setPlaceInterestCounts({});
      }
    } catch (error) {
      setError(`Places search failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search on load and when filters change - SIMPLIFIED
  useEffect(() => {
    if (map && !isInitializing && userLocation) {
      console.log('🔍 Auto-searching due to filter change with user location available');
      
      // Simple timeout for debouncing
      const searchTimeout = setTimeout(() => {
        searchNearbyPlaces();
      }, 500);
      
      return () => clearTimeout(searchTimeout);
    }
  }, [map, placeType, searchRadius, isInitializing, userLocation]);

  // REAL-TIME OPTIMIZED: Update marker colors when interests change
  useEffect(() => {
    if (markers.length > 0 && nearbyPlaces.length > 0) {
      // Simple timeout to batch updates
      const timeoutId = setTimeout(() => {
        updateMarkerColorsSmartly(interestedPlaces);
      }, 100); // Much shorter delay since we have real-time updates

      return () => clearTimeout(timeoutId);
    }
  }, [interestedPlaces, placeInterestCounts, selectedPlace]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.user-profile-section')) {
        setShowProfileMenu(false);
      }
      if (showDistanceMenu && !event.target.closest('.distance-dropdown-section')) {
        setShowDistanceMenu(false);
      }
      if (showPlaceTypeMenu && !event.target.closest('.place-type-dropdown-section')) {
        setShowPlaceTypeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu, showDistanceMenu, showPlaceTypeMenu]);

  const handleShowInterest = async (place) => {
    // EXPLICIT USER VALIDATION - Ensure we have a valid user
    if (!validateCurrentUser()) {
      console.error('❌ handleShowInterest: User validation failed, aborting interest operation');
      return;
    }

    // EXPLICIT USER ID LOGGING
    console.log('🔒 handleShowInterest - USER VALIDATION:', {
      userSub: user.sub,
      userEmail: user.email,
      userName: user.name,
      timestamp: new Date().toISOString()
    });

    const newInterestedPlaces = new Set(interestedPlaces);
    const newUserInterestExpirations = { ...userInterestExpirations };
    const isCurrentlyInterested = interestedPlaces.has(place.id);
    
    console.log('handleShowInterest called:', { 
      placeId: place.id, 
      placeName: place.displayName?.text, 
      isCurrentlyInterested,
      authenticatedUserId: user.sub // EXPLICIT LOG OF AUTHENTICATED USER
    });
    
    const action = isCurrentlyInterested ? 'remove' : 'add';
    const startTime = Date.now();
    
    try {
      if (isCurrentlyInterested) {
        // Remove interest - IMMEDIATE UI UPDATE
        console.log('🗑️ Removing interest for place:', place.id, 'for user:', user.sub);
        newInterestedPlaces.delete(place.id);
        delete newUserInterestExpirations[place.id];
        
        // EXPLICIT USER ID VALIDATION BEFORE DATABASE CALL
        if (!user.sub) {
          throw new Error('User ID is missing before removeUserInterest call');
        }
        
        console.log('🔒 REMOVING INTEREST - Final user validation before DB call:', {
          userId: user.sub,
          userEmail: user.email,
          placeId: place.id
        });
        
        const result = await supabaseIntegration.supabaseHelpers.removeUserInterest(user.sub, place.id);
        const duration = Date.now() - startTime;
        console.log('removeUserInterest result:', result);
        
        // Track this action for debugging
        if (window.trackShowInterestClick) {
          window.trackShowInterestClick({
            actionType: action,
            userId: user.sub, // EXPLICIT USER ID IN TRACKING
            userEmail: user.email, // EXPLICIT USER EMAIL IN TRACKING
            placeData: {
              place_id: place.id,
              place_name: place.displayName?.text,
              formattedAddress: place.formattedAddress,
              location: place.location
            },
            success: !result.error,
            responseTime: duration,
            result: result,
            error: result.error ? result.error.message : null
          });
        }
        
        if (result.error) {
          console.error('Database operation failed:', result.error);
          return;
        }
      } else {
        // Add interest - IMMEDIATE UI UPDATE
        console.log('➕ Adding interest for place:', place.id, 'for user:', user.sub);
        newInterestedPlaces.add(place.id);
        
        // Calculate expiration time immediately
        const expiresAt = new Date();
        expiresAt.setMinutes(expiresAt.getMinutes() + 60);
        newUserInterestExpirations[place.id] = expiresAt.toISOString();
        
        const placeData = {
          place_id: place.id,
          place_name: place.displayName?.text || 'Unknown Place',
          place_address: place.formattedAddress || '',
          place_location: {
            lat: place.location.latitude,
            lng: place.location.longitude
          }
        };
        
        // EXPLICIT USER ID VALIDATION BEFORE DATABASE CALL
        if (!user.sub) {
          throw new Error('User ID is missing before addUserInterest call');
        }
        
        console.log('🔒 ADDING INTEREST - Final user validation before DB call:', {
          userId: user.sub,
          userEmail: user.email,
          placeData: placeData
        });
        
        const result = await supabaseIntegration.supabaseHelpers.addUserInterest(
          user.sub, 
          placeData
        );
        const duration = Date.now() - startTime;
        console.log('✅ addUserInterest result:', result);
        
        // Track this action for debugging
        if (window.trackShowInterestClick) {
          window.trackShowInterestClick({
            actionType: action,
            userId: user.sub, // EXPLICIT USER ID IN TRACKING
            userEmail: user.email, // EXPLICIT USER EMAIL IN TRACKING
            placeData: {
              place_id: place.id,
              place_name: place.displayName?.text,
              formattedAddress: place.formattedAddress,
              location: place.location
            },
            success: !result.error,
            responseTime: duration,
            result: result,
            error: result.error ? result.error.message : null
          });
        }
        
        if (result.error) {
          console.error('Database operation failed:', result.error);
          return;
        }
      }
      
      // Refresh place interest counts after any interest change
      if (nearbyPlaces.length > 0 && loadPlaceInterestCounts) {
        const googlePlaceIds = nearbyPlaces.map(place => place.id);
        loadPlaceInterestCounts(googlePlaceIds);
      }
      
    } catch (error) {
      console.error('❌ Error updating interest:', error);
      const duration = Date.now() - startTime;
      
      // Track this error for debugging
      if (window.trackShowInterestClick) {
        window.trackShowInterestClick({
          actionType: action,
          userId: user.sub, // EXPLICIT USER ID IN TRACKING
          userEmail: user.email, // EXPLICIT USER EMAIL IN TRACKING
          placeData: {
            place_id: place.id,
            place_name: place.displayName?.text,
            formattedAddress: place.formattedAddress,
            location: place.location
          },
          success: false,
          responseTime: duration,
          result: null,
          error: error.message
        });
      }
    }
  };

  // Helper function to determine marker color based on place state
  const getMarkerColor = (place) => {
    const isUserInterested = interestedPlaces.has(place.id);
    const counts = placeInterestCounts[place.id];
    const hasFriendsInterested = counts && counts.friends > 0;
    const hasPublicInterested = counts && counts.public > 0;
    const isSelected = selectedPlace && selectedPlace.id === place.id;
    
    if (isUserInterested) {
      return '#10B981'; // Green for user interest
    } else if (isSelected) {
      return '#EAB308'; // Yellow for selected place
    } else if (hasFriendsInterested) {
      return '#3B82F6'; // Blue for friends' interest
    } else if (hasPublicInterested) {
      return '#8B5CF6'; // Purple for public interest
    } else {
      return '#F97316'; // Default orange
    }
  };

  // Smart marker color update - only updates when color actually changes
  const updateMarkerColorsSmartly = (interestedPlaceIds) => {
    let updatesCount = 0;
    
    markers.forEach((marker, index) => {
      const place = nearbyPlaces[index];
      if (place) {
        const newColor = getMarkerColor(place);
        const currentColor = marker.customCurrentColor || null;
        
        if (currentColor !== newColor) {
          marker.setIcon({
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2C9.58 2 6 5.58 6 10C6 16 14 26 14 26S22 16 22 10C22 5.58 18.42 2 14 2Z" fill="${newColor}"/>
                <circle cx="14" cy="10" r="3" fill="white"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(28, 28),
          });
          
          marker.customCurrentColor = newColor;
          updatesCount++;
        }
      }
    });
    
    if (updatesCount > 0) {
      console.log(`🎨 Updated ${updatesCount} marker colors (real-time optimized)`);
    }
  };

  // Smart map panning with debouncing
  const smartPanToPlace = (place) => {
    const now = Date.now();
    const timeSinceLastPan = now - lastPanTime;
    
    if (timeSinceLastPan < 300) {
      console.log('🗺️ Pan request debounced (too soon)');
      return;
    }
    
    if (map && place?.location) {
      console.log('🗺️ Smart panning to:', place.displayName?.text);
      map.panTo({
        lat: place.location.latitude,
        lng: place.location.longitude
      });
      setLastPanTime(now);
    }
  };

  const handlePrivacyModeChange = (newMode) => {
    setCurrentPrivacyMode(newMode);
  };

  const getPlaceTypeEmoji = (type) => {
    switch (type) {
      case 'restaurant': return '🍽️';
      case 'cafe': return '☕';
      case 'bar': return '🍺';
      case 'arts_culture': return '🎨';
      case 'dessert': return '🍰';
      case 'entertainment': return '🎮';
      case 'fast_food': return '🍔';
      case 'nature': return '🌳';
      case 'fitness': return '💪';
      default: return '🍽️';
    }
  };

  const getRadiusText = (radius) => {
    // Map radius values to their display text to match menu options
    const radiusMap = {
      805: '0.5 miles',
      1609: '1 mile',
      3219: '2 miles',
      8047: '5 miles',
      16094: '10 miles',
      32188: '20 miles'
    };
    
    return radiusMap[radius] || `${Math.round((radius / 1609.34) * 10) / 10} mi`;
  };

  const getPlaceTypeText = (type) => {
    const placeTypeMap = {
      'nature': 'Animals & Nature',
      'arts_culture': 'Arts & Culture',
      'bar': 'Beer & Wine',
      'cafe': 'Coffee & Tea',
      'dessert': 'Dessert & Ice Cream',
      'entertainment': 'Entertainment & Fun',
      'fast_food': 'Fast Food',
      'restaurant': 'Restaurants',
      'fitness': 'Sports & Fitness'
    };
    
    return placeTypeMap[type] || 'Restaurants';
  };

  const handleViewProfile = () => {
    setShowProfileMenu(false);
    navigate(`/profile/${user.sub}`);
  };

  const renderInterestCount = (placeId) => {
    const counts = placeInterestCounts[placeId];
    const isUserInterested = interestedPlaces.has(placeId);
    const userExpirationTime = userInterestExpirations[placeId];
    
    // If user is interested, show countdown timer instead of counts
    if (isUserInterested && userExpirationTime) {
      return <CountdownTimer expirationTime={userExpirationTime} />;
    }
    
    // If no counts or user is interested (but expired), don't show anything
    if (!counts || counts.total === 0 || isUserInterested) {
      return null;
    }

    // REAL-TIME OPTIMIZED: No need to check data age since we have real-time updates
    const { friends, public: publicCount } = counts;
    
    // Show interest counts for other users
    if (currentPrivacyMode === 'public') {
      if (friends > 0 && publicCount > 0) {
        return (
          <div className="interest-count-badge">
            <span className="interest-label">Interest: {friends} Friend{friends !== 1 ? 's' : ''} + {publicCount} Other{publicCount !== 1 ? 's' : ''}</span>
          </div>
        );
      } else if (friends > 0) {
        return (
          <div className="interest-count-badge">
            <span className="interest-label">Interest: {friends} Friend{friends !== 1 ? 's' : ''}</span>
          </div>
        );
      } else if (publicCount > 0) {
        return (
          <div className="interest-count-badge">
            <span className="interest-label">Interest: {publicCount} Other{publicCount !== 1 ? 's' : ''}</span>
          </div>
        );
      }
    } else {
      if (friends > 0) {
        return (
          <div className="interest-count-badge">
            <span className="interest-label">Interest: {friends} Friend{friends !== 1 ? 's' : ''}</span>
          </div>
        );
      }
    }

    return null;
  };

  // EXPLICIT USER VALIDATION FUNCTION
  const validateCurrentUser = () => {
    if (!user || !user.sub) {
      console.error('🚨 CRITICAL: No valid user found in MainAppV2', {
        user,
        hasUserSub: !!user?.sub,
        timestamp: new Date().toISOString()
      });
      return false;
    }
    
    console.log('✅ User validation passed:', {
      userId: user.sub,
      userEmail: user.email,
      userName: user.name,
      timestamp: new Date().toISOString()
    });
    
    return true;
  };

  // Log current user when component mounts or user changes
  useEffect(() => {
    console.log('🔐 MainAppV2 - Current user context:', {
      userId: user?.sub,
      userEmail: user?.email,
      userName: user?.name,
      hasSupabaseIntegration: !!supabaseIntegration,
      timestamp: new Date().toISOString()
    });
    
    validateCurrentUser();
  }, [user]);

  return (
    <div className="main-app-v2">
      {/* Floating Header */}
      <header className="floating-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">Popmates</div>
            <div className="tagline">See what's poppin' near you</div>
          </div>
          
          <div className="search-controls-inline">
            <div className="place-type-dropdown-section">
              <div 
                className="control-pill-dropdown"
                onClick={() => setShowPlaceTypeMenu(!showPlaceTypeMenu)}
              >
                <span className="dropdown-text">{getPlaceTypeText(placeType)}</span>
                <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              
              {showPlaceTypeMenu && (
                <div className="place-type-menu">
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('nature');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Animals & Nature
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('arts_culture');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Arts & Culture
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('bar');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Beer & Wine
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('cafe');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Coffee & Tea
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('dessert');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Dessert & Ice Cream
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('entertainment');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Entertainment & Fun
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('fast_food');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Fast Food
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('restaurant');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Restaurants
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setPlaceType('fitness');
                      setShowPlaceTypeMenu(false);
                    }}
                  >
                    Sports & Fitness
                  </button>
                </div>
              )}
            </div>

            <div className="distance-dropdown-section">
              <div 
                className="control-pill-dropdown"
                onClick={() => setShowDistanceMenu(!showDistanceMenu)}
              >
                <span className="dropdown-text">{getRadiusText(searchRadius)}</span>
                <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              
              {showDistanceMenu && (
                <div className="distance-menu">
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setSearchRadius(805);
                      setShowDistanceMenu(false);
                    }}
                  >
                    0.5 miles
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setSearchRadius(1609);
                      setShowDistanceMenu(false);
                    }}
                  >
                    1 mile
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setSearchRadius(3219);
                      setShowDistanceMenu(false);
                    }}
                  >
                    2 miles
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setSearchRadius(8047);
                      setShowDistanceMenu(false);
                    }}
                  >
                    5 miles
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setSearchRadius(16094);
                      setShowDistanceMenu(false);
                    }}
                  >
                    10 miles
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setSearchRadius(32188);
                      setShowDistanceMenu(false);
                    }}
                  >
                    20 miles
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="header-right-section">
            {/* Debug User Info Panel - Development Only */}
            {process.env.NODE_ENV === 'development' && (
              <div 
                className="debug-user-info"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '2px',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  backgroundColor: '#1F2937',
                  color: '#F3F4F6',
                  fontSize: '11px',
                  fontWeight: '500',
                  border: '1px solid #374151',
                  maxWidth: '200px'
                }}
              >
                <div style={{ color: '#10B981', fontWeight: '600' }}>🔐 LOGGED IN AS:</div>
                <div style={{ color: '#FBBF24' }}>ID: {user?.sub || 'Unknown'}</div>
                <div style={{ color: '#FBBF24' }}>Email: {user?.email || 'Unknown'}</div>
                <div style={{ color: '#FBBF24' }}>Name: {user?.name || 'Unknown'}</div>
              </div>
            )}


            
            {/* Notification Bell - Now using real-time WebSockets */}
            <NotificationBell supabaseIntegration={supabaseIntegration} user={user} />
            
            {/* Real-time connection indicator (for debugging) */}
            {process.env.NODE_ENV === 'development' && (
              <div 
                className="realtime-status"
                title={`Real-time interests: ${interestsConnected ? 'Connected' : 'Disconnected'}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: interestsConnected ? '#10B981' : '#EF4444',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                <div 
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: 'white'
                  }}
                />
                RT
              </div>
            )}
            
            {/* User Profile Section */}
            <div className="user-profile-section">
              <div 
                className="user-profile"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <img 
                  src={user.picture} 
                  alt={`${user.name}'s avatar`}
                  className="user-avatar"
                />
                <span className="user-name">{user.given_name}</span>
                <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              
              {showProfileMenu && (
                <div className="profile-menu">
                  <button 
                    className="menu-item"
                    onClick={handleViewProfile}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Profile
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowFriendsList(true);
                      setShowProfileMenu(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    Friends
                  </button>

                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowSettings(true);
                      setShowProfileMenu(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                    Settings
                  </button>
                  
                  {/* Admin Debug Panel - shown for admins and super admins */}
                  {canAccessDebugPanel(profile?.user_role) && (
                    <button 
                      className="menu-item"
                      onClick={() => {
                        setDebugPanelOpen(true);
                        setShowProfileMenu(false);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                      </svg>
                      Admin
                    </button>
                  )}
                  
                  {/* Submit Feedback - shown for testers only */}
                  {isTester(profile?.user_role) && (
                    <button 
                      className="menu-item"
                      onClick={() => {
                        setFeedbackPanelOpen(true);
                        setShowProfileMenu(false);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"></path>
                      </svg>
                      Feedback
                    </button>
                  )}
                  
                  <button 
                    className="menu-item logout"
                    onClick={onLogout}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16,17 21,12 16,7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Integrated Layout */}
      <main className="app-main-integrated">
        <div className="map-container">
          {isInitializing && !error && (
            <div className="map-loading">
              <div className="loading-spinner"></div>
              <p>Loading map...</p>
            </div>
          )}
          {error && (
            <div className="map-error">
              <div className="error-icon">❌</div>
              <p>{error}</p>
            </div>
          )}
          <div ref={mapRef} className="map" style={{ display: (isInitializing || error) ? 'none' : 'block' }} />
        </div>

        {/* Floating Places Panel */}
        <div className="floating-places-panel">
          {/* Mobile Search Controls - only visible on mobile */}
          <div className="mobile-search-controls">
            <div className="control-pill">
              <select value={placeType} onChange={(e) => setPlaceType(e.target.value)}>
                <option value="nature">Animals & Nature</option>
                <option value="arts_culture">Arts & Culture</option>
                <option value="bar">Beer & Wine</option>
                <option value="cafe">Coffee & Tea</option>
                <option value="dessert">Dessert & Ice Cream</option>
                <option value="entertainment">Entertainment & Fun</option>
                <option value="fast_food">Fast Food</option>
                <option value="restaurant">Restaurants</option>
                <option value="fitness">Sports & Fitness</option>
              </select>
            </div>

            <div className="control-pill">
              <select value={searchRadius} onChange={(e) => setSearchRadius(Number(e.target.value))}>
                <option value={805}>0.5 miles</option>
                <option value={1609}>1 mile</option>
                <option value={3219}>2 miles</option>
                <option value={8047}>5 miles</option>
                <option value={16094}>10 miles</option>
                <option value={32188}>20 miles</option>
              </select>
            </div>
          </div>

          <div className="places-scroll">
            {loading && (
              <div className="loading-places">
                <div className="loading-spinner small"></div>
                <p>Finding places nearby...</p>
              </div>
            )}
            {nearbyPlaces.length === 0 && !loading && (
              <div className="no-places">
                <div className="no-places-emoji">🔍</div>
                <p>No places found nearby</p>
                <p>Try expanding your search radius</p>
              </div>
            )}
            
            {nearbyPlaces.map((place) => {
              const isInterested = interestedPlaces.has(place.id);
              const distance = getPlaceDistance(place);
              const isExpanded = expandedPlace?.id === place.id;
              return (
                <div key={place.id} id={`place-${place.id}`}>
                  <div 
                    className={`place-card-compact ${selectedPlace?.id === place.id ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => {
                      // Toggle expanded details for this place
                      if (isExpanded) {
                        setExpandedPlace(null);
                      } else {
                        setExpandedPlace(place);
                      }
                      
                      // Also set as selected place and center map
                      setSelectedPlace(place);
                      smartPanToPlace(place);
                    }}
                  >
                    <div className="place-main-info">
                      <div className="place-header">
                        <h4>{place.displayName?.text || 'Unknown Place'}</h4>
                        <span className={`expand-indicator ${isExpanded ? 'expanded' : ''}`}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                      <div className="place-meta-row">
                        {distance && (
                          <span className="place-distance">📍 {distance}</span>
                        )}
                        {place.rating && (
                          <span className="place-rating">⭐ {place.rating}</span>
                        )}

                        {renderInterestCount(place.id)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expandable Details Section */}
                  {isExpanded && (
                    <div className="place-expanded-details">
                      <div className="expanded-content">
                        <div className="expanded-address">
                          <strong>Address:</strong> {place.formattedAddress || 'Address not available'}
                        </div>
                        {place.rating && (
                          <div className="expanded-rating">
                            <strong>Rating:</strong> ⭐ {place.rating} / 5
                          </div>
                        )}

                        <div className="expanded-actions">
                          <button 
                            className={`interest-button ${isInterested ? 'interested' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card collapse
                              handleShowInterest(place);
                            }}
                          >
                            {isInterested ? '✅ I\'m interested!' : 'Show interest'}
                          </button>
                          <div className="interest-status-text">
                            {isInterested ? 
                              `Looking for others...` :
                              `Let others know you're thinking of going here`
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>



      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Settings</h2>
              <button 
                className="close-button"
                onClick={() => setShowSettings(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="settings-content">
                <div className="settings-section">
                  <h3>Privacy Settings</h3>
                  <p>Control who can see your location interests</p>
                  <div className="privacy-settings-container">
                    <PrivacyModeDropdown 
                      user={user}
                      profile={profile}
                      supabaseIntegration={supabaseIntegration}
                      onPrivacyModeChange={handlePrivacyModeChange}
                      displayMode="radio"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Friends List */}
      <FriendsList 
        user={user}
        isOpen={showFriendsList}
        onClose={() => setShowFriendsList(false)}
      />

      {/* Admin Debug Panel */}
      <AdminDebugPanel 
        user={user}
        profile={profile}
        supabaseLoading={false}
        supabaseError={null}
        updateUserProfile={supabaseIntegration.updateUserProfile}
        logUserEvent={supabaseIntegration.logUserEvent}
        getAllProfiles={supabaseIntegration.getAllProfiles}
        getLogs={supabaseIntegration.getLogs}
        supabaseHelpers={supabaseIntegration.supabaseHelpers}
        DatabaseTestComponent={DatabaseTest}
        isOpen={debugPanelOpen}
        onClose={() => setDebugPanelOpen(false)}
        onOpenFeedback={() => setFeedbackPanelOpen(true)}
      />

      {/* Feedback Panel */}
      <FeedbackPanel 
        user={user}
        profile={profile}
        supabaseIntegration={supabaseIntegration}
        isOpen={feedbackPanelOpen}
        onClose={() => setFeedbackPanelOpen(false)}
      />

      {/* Onboarding Manager - checks for unanswered questions and shows modal */}
      <OnboardingManager 
        user={user}
        supabaseHelpers={supabaseIntegration.supabaseHelpers}
        isEnabled={true}
      />
    </div>
  );
};

export default MainAppV2; 